import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { ArrowLeft, RefreshCw, Users, XOctagon, CheckCircle2, TrendingUp } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

/** /reactivation-metrics — admin-only dashboard tracking the 5-touch nurture
 *  sequence: sent / pending / failed / opt-out counts per touch, plus the
 *  overall lead → booking funnel. Use this to spot the weakest touch and
 *  A/B swap it. */
const TOUCH_LABELS = {
  case_study: "Day 2 · Alicia case study",
  mistakes: "Day 5 · 3 mistakes killing campaigns",
  copy_reveal: "Day 9 · Industry SMS copy reveal",
  last_touch: "Day 14 · Last touch",
};

export default function ReactivationMetrics() {
  const { user, loading: authLoading } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data: body } = await api.get("/admin/reactivation/metrics");
      setData(body);
    } catch (err) {
      toast.error(formatApiError(err) || "Load failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (!authLoading && user?.role === "admin") load(); }, [authLoading, user]);
  if (!authLoading && user?.role !== "admin") return <Navigate to="/login" replace />;

  return (
    <div className="min-h-screen bg-neutral-50" data-testid="reactivation-metrics-page">
      <div className="max-w-6xl mx-auto px-6 py-10">
        <div className="flex items-center justify-between mb-8">
          <div>
            <Link to="/users" className="inline-flex items-center gap-1 text-xs text-neutral-500 hover:text-neutral-800 mb-2 no-underline">
              <ArrowLeft className="w-3 h-3" /> back to admin
            </Link>
            <h1 className="font-display text-3xl font-bold">Reactivation nurture metrics</h1>
            <p className="text-sm text-neutral-600 mt-1">Per-touch delivery + lead → booking funnel. Spot the weakest email and swap its copy.</p>
          </div>
          <button onClick={load} disabled={loading} data-testid="reactivation-metrics-refresh"
                  className="inline-flex items-center gap-2 px-4 py-2 border border-neutral-300 hover:border-neutral-500 text-sm font-semibold disabled:opacity-50">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>

        {!data ? (
          <div className="bg-white border border-neutral-200 p-10 text-center text-neutral-500 text-sm">
            {loading ? "Loading…" : "No data yet."}
          </div>
        ) : (
          <>
            {/* Top-line funnel */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <StatCard testId="metric-leads" label="Leads captured" value={data.totals.leads_captured} icon={<Users className="w-4 h-4" />} tone="ink" />
              <StatCard testId="metric-booked" label="Booked / rebooked" value={data.totals.leads_booked} icon={<CheckCircle2 className="w-4 h-4" />} tone="emerald" />
              <StatCard testId="metric-conversion" label="Lead → booking" value={`${data.totals.lead_to_booking_pct}%`} icon={<TrendingUp className="w-4 h-4" />} tone="emerald" />
              <StatCard testId="metric-optout" label="Opt-outs" value={data.totals.opt_outs} icon={<XOctagon className="w-4 h-4" />} tone="amber" />
            </div>

            {/* Per-touch table */}
            <div className="bg-white border border-neutral-200 overflow-hidden">
              <div className="px-5 py-4 border-b border-neutral-200 flex items-center justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-mono">Per-touch performance</div>
                  <h2 className="font-display text-lg font-bold">4-touch nurture sequence</h2>
                </div>
                <div className="text-[11px] text-neutral-500 font-mono">
                  Updated {new Date(data.generated_at).toLocaleString()}
                </div>
              </div>
              <table className="w-full text-sm" data-testid="reactivation-metrics-table">
                <thead className="bg-neutral-50 border-b border-neutral-200 text-[10px] uppercase tracking-widest text-neutral-500 font-mono">
                  <tr>
                    <th className="text-left px-5 py-3">Touch</th>
                    <th className="text-right px-3 py-3">Sent</th>
                    <th className="text-right px-3 py-3">Pending</th>
                    <th className="text-right px-3 py-3">Failed</th>
                    <th className="text-right px-3 py-3">Opt-out</th>
                    <th className="text-right px-5 py-3">Delivery</th>
                  </tr>
                </thead>
                <tbody>
                  {data.touches.map((t) => {
                    // delivery_pct is null until at least one touch is resolved (sent/failed/cancelled)
                    const hasResolved = t.delivery_pct !== null && t.delivery_pct !== undefined;
                    const tone = !hasResolved
                      ? "text-neutral-400"
                      : t.delivery_pct >= 90 ? "text-emerald-700"
                      : t.delivery_pct >= 70 ? "text-amber-700"
                      : "text-rose-700";
                    return (
                      <tr key={t.touch_key} data-testid={`touch-row-${t.touch_key}`} className="border-b border-neutral-100 hover:bg-neutral-50">
                        <td className="px-5 py-4">
                          <div className="font-semibold text-neutral-800">{TOUCH_LABELS[t.touch_key] || t.touch_key}</div>
                          <div className="text-[11px] text-neutral-500 font-mono mt-0.5">+{t.day_offset} days · {t.total_targeted} total targeted</div>
                        </td>
                        <td className="text-right px-3 py-4 font-mono font-bold text-emerald-700">{t.sent}</td>
                        <td className="text-right px-3 py-4 font-mono text-neutral-500">{t.pending}</td>
                        <td className="text-right px-3 py-4 font-mono text-rose-600">{t.failed}</td>
                        <td className="text-right px-3 py-4 font-mono text-amber-600">{t.cancelled_optout}</td>
                        <td className={`text-right px-5 py-4 font-mono font-bold ${tone}`}>
                          {hasResolved ? `${t.delivery_pct}%` : "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {data.touches.every((t) => t.total_targeted === 0) && (
                <div className="px-5 py-8 text-center text-sm text-neutral-500 italic">
                  No nurture touches recorded yet. Metrics populate as the daily cron fires (15:00 UTC · 10am ET) against real /reactivation-lead submissions.
                </div>
              )}
            </div>

            <p className="mt-6 text-xs text-neutral-500 leading-relaxed">
              <strong>Delivery %</strong> = sent / (sent + failed + cancelled). Anything under 90% signals a copy or Resend issue.
              <br /><strong>Lead → booking %</strong> is calculated from leads with status <code>booked</code>, <code>won</code>, or <code>customer</code> — update lead status on <Link to="/leads" className="text-emerald-700">/leads</Link> as you close deals so this stays accurate.
            </p>
          </>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value, icon, tone, testId }) {
  const toneClass = tone === "emerald" ? "text-emerald-700 border-emerald-200 bg-emerald-50"
                  : tone === "amber"   ? "text-amber-700 border-amber-200 bg-amber-50"
                  : "text-neutral-800 border-neutral-200 bg-white";
  return (
    <div data-testid={testId} className={`border-2 ${toneClass.split(" ").slice(1).join(" ")} p-5`}>
      <div className="flex items-center justify-between mb-2">
        <div className={`text-[10px] uppercase tracking-widest font-mono ${toneClass.split(" ")[0]}`}>{label}</div>
        <div className={toneClass.split(" ")[0]}>{icon}</div>
      </div>
      <div className={`text-3xl font-black font-display ${toneClass.split(" ")[0]}`}>{value}</div>
    </div>
  );
}
